import React, { useState, type FormEvent } from "react";
import { Outlet, useLocation, useNavigate } from "@tanstack/react-router";
import {
  Activity,
  Bell,
  Boxes,
  Building2,
  CreditCard,
  Receipt,
  Gift,
  Puzzle,
  Database,
  FileSpreadsheet,
  Flag,
  Globe,
  HardDrive,
  Headphones,
  KeyRound,
  LayoutDashboard,
  Images,
  Settings,
  FileText,
  Files,
  Menu,
  Layers,
  LogOut,
  Mail,
  MessageSquare,
  Phone,
  Search,
  Sliders,
  UserCheck,
  Users,
} from "lucide-react";
import {
  AppShell,
  Avatar,
  AvatarFallback,
  Button,
  CommandPalette,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Label,
  ThemeToggle,
  type NavGroup,
} from "@bs/ui";
import { PasswordInput } from "@bs/ui";
import { changePassword, signOut, type PlatformUser } from "../lib/auth.ts";
import { UserContext } from "../lib/user-context.tsx";
import { PlatformNotifications } from "../components/PlatformNotifications.tsx";

interface LayoutProps {
  user: PlatformUser;
  onLogout: () => void;
}

const navGroups: NavGroup[] = [
  {
    items: [{ label: "Overview", href: "/", icon: LayoutDashboard }],
  },
  {
    label: "Commerce & Stores",
    items: [
      { label: "Tenants", href: "/tenants", icon: Building2 },
      { label: "Domains", href: "/domains", icon: Globe },
      { label: "Plans", href: "/plans", icon: CreditCard },
      { label: "Invoices & transactions", href: "/ledger", icon: Receipt },
      { label: "Promotions", href: "/promotions", icon: Gift },
      { label: "Marketplace", href: "/marketplace", icon: Puzzle },
      { label: "Signups Funnel", href: "/signups", icon: Users },
      { label: "Themes", href: "/templates", icon: Layers },
    ],
  },
  {
    label: "Storefront",
    items: [
      { label: "Pages", href: "/storefront/pages", icon: Files },
      { label: "Blogs", href: "/storefront/blog", icon: FileText },
      { label: "Navigation", href: "/storefront/navigation", icon: Menu },
      { label: "Media", href: "/storefront/media", icon: Images },
      { label: "Settings", href: "/storefront/settings", icon: Settings },
    ],
  },
  {
    label: "Operations & Support",
    items: [
      { label: "Support Sessions", href: "/support", icon: Headphones },
      { label: "System & Queues", href: "/system", icon: Activity },
      { label: "Notifications", href: "/notifications", icon: Bell },
      { label: "Integrations", href: "/integrations", icon: Boxes },
      { label: "Quotas & Tiers", href: "/quotas", icon: Sliders },
      { label: "Feature Flags", href: "/features", icon: Flag },
    ],
  },
  {
    label: "Administration",
    items: [
      { label: "Platform Staff", href: "/staff", icon: UserCheck },
      { label: "Audit Log", href: "/audit", icon: FileSpreadsheet },
    ],
  },
];

export function Layout({ user, onLogout }: LayoutProps) {
  const location = useLocation();
  // The staff list and Storefront are admin-only on the server; do not show support staff a page that would be refused.
  const groups = navGroups
    .map((g) => ({
      ...g,
      items: g.items.filter((i) => !((i.href === "/staff" || i.href.startsWith("/storefront")) && user.role === "platform_support")),
    }))
    .filter((g) => g.items.length > 0);
  const navigate = useNavigate();

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setError(null);
    setSuccess(false);
    setBusy(false);
  };

  const handlePasswordSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 10) {
      setError("New password must be at least 10 characters.");
      return;
    }
    if (newPassword.length > 128) {
      setError("New password cannot exceed 128 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setBusy(true);
    setError(null);

    const res = await changePassword(currentPassword, newPassword);
    setBusy(false);

    if (!res.ok) {
      setError(res.message);
      return;
    }

    setSuccess(true);
  };

  const handleSignOut = async () => {
    await signOut();
    onLogout();
    navigate({ to: "/login" });
  };

  const [commandPaletteOpen, setCommandPaletteOpen] = useState(false);

  const commandActions = [
    { id: "overview", label: "Overview", icon: LayoutDashboard, onSelect: () => navigate({ to: "/" }), group: "Navigation" },
    { id: "tenants", label: "Tenants", icon: Building2, onSelect: () => navigate({ to: "/tenants" }), group: "Commerce" },
    { id: "tenant-create", label: "Create Store", icon: Building2, onSelect: () => navigate({ to: "/tenants/create" }), group: "Commerce" },
    { id: "domains", label: "Domains", icon: Globe, onSelect: () => navigate({ to: "/domains" }), group: "Commerce" },
    { id: "plans", label: "Plans", icon: CreditCard, onSelect: () => navigate({ to: "/plans" }), group: "Commerce" },
    { id: "ledger", label: "Invoices & transactions", icon: Receipt, onSelect: () => navigate({ to: "/ledger" }), group: "Commerce" },
    { id: "promotions", label: "Promotions", icon: Gift, onSelect: () => navigate({ to: "/promotions" }), group: "Commerce" },
    { id: "marketplace", label: "Marketplace", icon: Puzzle, onSelect: () => navigate({ to: "/marketplace" }), group: "Commerce" },
    { id: "signups", label: "Signups Funnel", icon: Users, onSelect: () => navigate({ to: "/signups" }), group: "Commerce" },
    { id: "templates", label: "Themes Library", icon: Layers, onSelect: () => navigate({ to: "/templates" }), group: "Commerce" },
    { id: "support", label: "Support Sessions", icon: Headphones, onSelect: () => navigate({ to: "/support" }), group: "Operations" },
    { id: "system", label: "System & Queues", icon: Activity, onSelect: () => navigate({ to: "/system" }), group: "Operations" },
    { id: "integrations", label: "Integrations Hub", icon: Boxes, onSelect: () => navigate({ to: "/integrations" }), group: "Operations" },
    { id: "notification-manager", label: "Notifications (events and branding)", icon: Bell, onSelect: () => navigate({ to: "/notifications" }), group: "Operations" },
    { id: "notifications", label: "Notification Channels", icon: Mail, onSelect: () => navigate({ to: "/integrations/notifications" }), group: "Operations" },
    { id: "email", label: "Email (ZeptoMail / SMTP)", icon: Mail, onSelect: () => navigate({ to: "/integrations/notifications/email" }), group: "Operations" },
    { id: "sms", label: "SMS (Zoho CPaaS)", icon: Phone, onSelect: () => navigate({ to: "/integrations/notifications/sms" }), group: "Operations" },
    { id: "whatsapp", label: "WhatsApp (Zoho CPaaS)", icon: MessageSquare, onSelect: () => navigate({ to: "/integrations/notifications/whatsapp" }), group: "Operations" },
    { id: "storage", label: "Storage Connections", icon: HardDrive, onSelect: () => navigate({ to: "/integrations/storage" }), group: "Operations" },
    { id: "payments", label: "Payment Gateways", icon: CreditCard, onSelect: () => navigate({ to: "/integrations/payments" }), group: "Operations" },
    { id: "billing", label: "Platform billing", icon: CreditCard, onSelect: () => navigate({ to: "/integrations/billing" }), group: "Operations" },
    { id: "quotas", label: "Quotas & Tiers", icon: Sliders, onSelect: () => navigate({ to: "/quotas" }), group: "Operations" },
    { id: "features", label: "Feature Flags", icon: Flag, onSelect: () => navigate({ to: "/features" }), group: "Operations" },
    ...(user.role !== "platform_support"
      ? [{ id: "staff", label: "Platform Staff", icon: UserCheck, onSelect: () => navigate({ to: "/staff" }), group: "Administration" }]
      : []),
    { id: "audit", label: "Audit Log", icon: FileSpreadsheet, onSelect: () => navigate({ to: "/audit" }), group: "Administration" },
  ];

  return (
    <UserContext.Provider value={user}>
      <AppShell
        brand={
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <span className="text-sm font-bold tracking-tight">Super Admin</span>
          </div>
        }
        groups={groups}
        activeHref={location.pathname.startsWith("/integrations") ? "/integrations" : location.pathname}
        topRight={
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCommandPaletteOpen(true)}
              className="flex h-8 w-44 sm:w-64 items-center justify-between rounded-lg border border-border bg-muted/40 hover:bg-muted/70 px-2.5 text-xs text-muted-foreground hover:text-foreground transition-all shadow-2xs focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              aria-label="Quick search (Ctrl+K)"
            >
              <span className="flex items-center gap-2">
                <Search className="size-3.5 shrink-0 opacity-70" />
                <span className="truncate">Quick search...</span>
              </span>
              <kbd className="pointer-events-none hidden h-4.5 select-none items-center gap-1 rounded border border-border/60 bg-background/80 px-1.5 font-mono text-[10px] font-medium text-muted-foreground sm:inline-flex">
                Ctrl K
              </kbd>
            </button>
            <PlatformNotifications />
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <button
                    type="button"
                    className="group flex items-center gap-2 rounded-full p-1 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label="Staff account menu"
                  >
                    <Avatar size="sm" className="border border-border-soft bg-muted">
                      <AvatarFallback className="bg-muted text-foreground font-semibold text-xs">{(user.name ?? user.email ?? "SA").slice(0, 2).toUpperCase()}</AvatarFallback>
                    </Avatar>
                  </button>
                }
              />
              <DropdownMenuContent align="end" className="w-56 p-1.5 shadow-lg">
                <DropdownMenuLabel className="px-2 py-1.5 text-xs">
                  <p className="font-semibold text-foreground truncate">{user.name || "Platform Staff"}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{user.email}</p>
                  <span className="mt-1 inline-block text-[10px] uppercase font-semibold text-primary tracking-wider">
                    {user.role.replace("platform_", "")}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => {
                    resetPasswordForm();
                    setPasswordOpen(true);
                  }}
                >
                  <KeyRound className="size-3.5 mr-2" />
                  <span>Password</span>
                </DropdownMenuItem>
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Sliders className="size-3.5 mr-2" />
                    <span>Theme</span>
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="w-36 p-1">
                    <ThemeToggle variant="menu" />
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                <DropdownMenuSeparator />
                <DropdownMenuItem variant="destructive" onClick={handleSignOut}>
                  <LogOut className="size-3.5 mr-2" />
                  <span>Sign out</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        }
        banner={
          <div className="flex items-center justify-between bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-400 border-b border-amber-500/20">
            <span>Super Admin Mode · Every privileged action is audited under platform BYPASSRLS</span>
            <span className="text-muted-foreground">Staff: <strong className="font-semibold text-foreground">{user.email}</strong> · {user.role.replace("platform_", "")}</span>
          </div>
        }
      >
        <Outlet />
      </AppShell>

      <CommandPalette
        open={commandPaletteOpen}
        onOpenChange={setCommandPaletteOpen}
        actions={commandActions}
      />

      <Dialog open={passwordOpen} onOpenChange={(open) => {
        if (!open) resetPasswordForm();
        setPasswordOpen(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change Platform Password</DialogTitle>
            <DialogDescription>
              Update your password. All other active platform sessions will be signed out.
            </DialogDescription>
          </DialogHeader>

          {success ? (
            <div className="space-y-4 py-2">
              <div className="rounded-md bg-emerald-500/10 p-3 text-xs text-emerald-700 border border-emerald-500/20 font-medium">
                Your password has been changed successfully. Other active sessions have been signed out.
              </div>
              <DialogFooter>
                <Button onClick={() => setPasswordOpen(false)} size="sm">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="current-pw">Current Password</Label>
                <PasswordInput
                  id="current-pw"
                  
                  required
                  autoFocus
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="new-pw">New Password (min 10 characters)</Label>
                <PasswordInput
                  id="new-pw"
                  
                  required
                  minLength={10}
                  maxLength={128}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pw">Confirm New Password</Label>
                <PasswordInput
                  id="confirm-pw"
                  
                  required
                  minLength={10}
                  maxLength={128}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              {error && (
                <div role="alert" className="rounded-md bg-destructive/10 p-2 text-xs text-destructive font-medium border border-destructive/20">
                  {error}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="ghost" size="sm" onClick={() => setPasswordOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" size="sm" disabled={busy || !currentPassword || !newPassword || !confirmPassword}>
                  {busy ? "Updating…" : "Update Password"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </UserContext.Provider>
  );
}
